import { useState } from "react";
import mainPhoto from "../assets/about-section.jpeg";
import recoveryPhoto from "../assets/recovery.jpeg"; // New photo 1
import officePhoto from "../assets/office.jpeg"; // New photo 2

export default function About() {
  const [selectedImg, setSelectedImg] = useState(null);

  const images = [
    { src: mainPhoto, alt: "Ruwan Indika - Present" },
    { src: recoveryPhoto, alt: "The Journey" },
    { src: officePhoto, alt: "Ruwan at AIA Office" },
  ];

  // Function to handle smooth scrolling to contact section
  const scrollToContact = () => {
    const contactSection = document.getElementById("contact");
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section id="about" className="relative mx-auto max-w-7xl px-6 py-24">
      {/* --- Lightbox Overlay --- */}
      {selectedImg && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-4"
          onClick={() => setSelectedImg(null)}
        >
          <button className="absolute top-10 right-10 text-white text-4xl hover:text-red-500 transition-colors">
            &times;
          </button>
          <img
            src={selectedImg}
            className="max-h-[90vh] max-w-full rounded-lg shadow-2xl transition-transform duration-300 scale-100"
            alt="Enlarged view"
          />
        </div>
      )}

      {/* --- Section Header --- */}
      <div className="flex flex-col items-center text-center mb-16">
        <span className="text-red-600 font-bold tracking-widest uppercase text-sm">
          My Story
        </span>
        <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
          From Stillness to Strength
        </h2>
        <div className="h-1.5 w-24 bg-red-600 mt-6 rounded-full"></div>
      </div>

      <div className="grid gap-16 lg:grid-cols-2 lg:items-center">
        {/* --- Left Column: Narrative --- */}
        <div className="space-y-10">
          <div className="flex gap-6">
            <div className="flex-none flex flex-col items-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-50 text-red-600 ring-1 ring-red-200 font-bold">
                1
              </div>
              <div className="w-px h-full bg-slate-200 mt-2"></div>
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-900">
                The Challenge
              </h3>
              <p className="mt-2 text-slate-600 leading-relaxed">
                Told he would{" "}
                <span className="text-red-600 font-bold">never walk again</span>
                , Ruwan faced a physical and financial crisis that changed his
                perspective on life forever.
              </p>
            </div>
          </div>

          <div className="flex gap-6">
            <div className="flex-none flex flex-col items-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-50 text-red-600 ring-1 ring-red-200 font-bold">
                2
              </div>
              <div className="w-px h-full bg-slate-200 mt-2"></div>
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-900">
                The Willpower
              </h3>
              <p className="mt-2 text-slate-600 leading-relaxed">
                Through unwavering determination, he defied the doctors. Today,
                he walks with a mission to protect others from the hardships he
                once endured.
              </p>
            </div>
          </div>

          <div className="rounded-2xl bg-red-600 p-8 text-white shadow-xl shadow-red-200">
            <h3 className="text-xl font-bold uppercase tracking-wide">
              The Mission
            </h3>
            <p className="mt-3 text-red-50 italic text-lg leading-relaxed">
              "I don't just sell insurance; I provide the shield I wish my
              family had. No one should face sickness and financial ruin at the
              same time."
            </p>
          </div>
        </div>

        {/* --- Right Column: Visual Grid --- */}
        <div className="grid grid-cols-2 grid-rows-2 gap-4 h-[500px]">
          <div
            className="relative col-span-1 row-span-2 overflow-hidden rounded-2xl shadow-xl cursor-zoom-in group"
            onClick={() => setSelectedImg(images[0].src)}
          >
            <img
              src={images[0].src}
              className="h-full w-full object-cover transition duration-500 group-hover:scale-110"
              alt=""
            />
            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <span className="text-white bg-red-600 px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                View Full Image
              </span>
            </div>
          </div>

          {images.slice(1).map((img, index) => (
            <div
              key={index}
              className="relative overflow-hidden rounded-2xl shadow-lg border-2 border-white cursor-zoom-in group"
              onClick={() => setSelectedImg(img.src)}
            >
              <img
                src={img.src}
                className="h-full w-full object-cover transition duration-500 group-hover:scale-110"
                alt=""
              />
              <div className="absolute inset-0 bg-red-900/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            </div>
          ))}
        </div>
      </div>

      {/* --- New Call to Action (CTA) Section --- */}
      <div className="mt-20 rounded-3xl bg-slate-900 p-8 md:p-12 shadow-2xl overflow-hidden relative">
        {/* Subtle Decorative Background Circle */}
        <div className="absolute -top-10 -right-10 h-40 w-40 bg-red-600 rounded-full blur-3xl opacity-20"></div>

        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="text-center md:text-left">
            <h3 className="text-2xl md:text-3xl font-bold text-white">
              Protect your future with someone who knows the value of a second
              chance.
            </h3>
            <p className="mt-4 text-slate-300 max-w-xl">
              Don't wait for an unfortunate situation to plan your protection.
              Let's discuss a tailored AIA health and wealth solution today.
            </p>
          </div>

          <button
            onClick={scrollToContact}
            className="whitespace-nowrap bg-red-600 hover:bg-red-700 text-white font-bold py-4 px-8 rounded-full shadow-lg transition-all transform hover:scale-105 active:scale-95 flex items-center gap-2"
          >
            <span>Book a Free Consultation</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
}
